import get from 'lodash/get';
import { getVulnId, getVulnValue, normalizeVulnId as normalizeId } from 'Common/Functions/vulnerabilityId';

export { getVulnId, getVulnValue };

const getPositiveInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return null;
  return Math.trunc(parsed);
};

export const getVulnName = (vuln) => {
  const value = getVulnValue(vuln);
  return get(value, 'name', get(vuln, 'name', 'Unnamed vulnerability'));
};

const getGroupCountFromVuln = (vuln, groupCountOverrides = {}) => {
  const id = normalizeId(getVulnId(vuln));
  const override = id ? getPositiveInteger(groupCountOverrides[id]) : null;
  if (override !== null) return override;

  const value = getVulnValue(vuln);
  return getPositiveInteger(get(value, 'group_count', get(vuln, 'group_count', null)));
};

const getGroupKeys = (vuln) => {
  const value = getVulnValue(vuln);
  return [
    getVulnId(vuln),
    get(value, 'group_id', get(vuln, 'group_id', null)),
    get(value, 'group_vuln_id', get(vuln, 'group_vuln_id', null))
  ].map(normalizeId).filter(Boolean);
};

const isMainVuln = (vuln) => get(getVulnValue(vuln), 'is_main', get(vuln, 'is_main', false)) === true;

export const getDeleteImpact = (vulnsSelected = [], options = {}) => {
  const { groupCountOverrides = {} } = options;
  const groupsById = new Map();
  const coveredGroupKeys = new Set();

  vulnsSelected
    .filter(isMainVuln)
    .forEach((vuln) => {
      const id = normalizeId(getVulnId(vuln));
      if (!id || groupsById.has(id)) return;

      const count = getGroupCountFromVuln(vuln, groupCountOverrides);
      const groupKeys = getGroupKeys(vuln);
      groupKeys.forEach((key) => coveredGroupKeys.add(key));

      groupsById.set(id, {
        id,
        name: getVulnName(vuln),
        count,
        hasKnownCount: count !== null,
        groupKeys
      });
    });

  const individualsById = new Map();
  vulnsSelected.forEach((vuln) => {
    const id = normalizeId(getVulnId(vuln));
    if (!id || groupsById.has(id)) return;

    const isCoveredBySelectedMain = getGroupKeys(vuln).some((key) => coveredGroupKeys.has(key));
    if (isCoveredBySelectedMain) return;

    individualsById.set(id, {
      id,
      name: getVulnName(vuln)
    });
  });

  const groups = Array.from(groupsById.values());
  const individuals = Array.from(individualsById.values());
  const unknownGroups = groups.filter((group) => !group.hasKnownCount);
  const knownGroupsTotal = groups.reduce((total, group) => total + (group.count || 0), 0);
  const total = knownGroupsTotal + individuals.length;

  return {
    groups,
    individuals,
    unknownGroups,
    total,
    hasGroupedDeletion: groups.length > 0,
    hasUnknownCounts: unknownGroups.length > 0
  };
};
